import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import { View } from 'react-big-calendar';
import { getReferenceString } from '@medplum/core';
import { Practitioner, PractitionerRole } from '@medplum/fhirtypes';
import {
  CANONICAL_US_TIMEZONE_BY_GROUP,
  type UsTimezoneGroup,
  US_TIMEZONE_GROUP_BY_IANA,
  US_TIMEZONE_LABEL_BY_GROUP,
} from '@bariendo/lib/constants';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

// TODO: think about moving this to a more generic file as the handling with timezone increasing complexity
const US_TIMEZONE_PRIORITY: string[] = [
  'America/New_York', // ET
  'America/Chicago', // CT
  'America/Denver', // MT
  'America/Phoenix', // AZ (no DST)
  'America/Los_Angeles', // PT
  'America/Anchorage', // Alaska
  'Pacific/Honolulu', // Hawaii
];

const usRank = new Map(US_TIMEZONE_PRIORITY.map((tz, i) => [tz, i]));

// Get browser timezone
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York'; // Fallback
  }
}

/** Maps a US IANA id (e.g. America/Detroit) to the canonical id for its {@link UsTimezoneGroup}; passes through others. */
export function normalizeUsTimezoneToCanonical(timezone: string): string {
  const group = US_TIMEZONE_GROUP_BY_IANA[timezone];
  return group ? CANONICAL_US_TIMEZONE_BY_GROUP[group] : timezone;
}

/** Maps a canonical/US IANA id to its friendly US label when available; passes through others. */
export function timezoneToFriendlyLabel(timezone: string): string {
  const group = US_TIMEZONE_GROUP_BY_IANA[timezone];
  return group ? US_TIMEZONE_LABEL_BY_GROUP[group] : timezone;
}

type TimezoneSelectOption = { value: string; label: string };

/**
 * Maps a list of IANA timezone ids to select options. US ids are collapsed to one row per
 * {@link UsTimezoneGroup} (canonical value + shared label); other ids stay one row each.
 */
function groupUsTimezonesToSelectOptions(
  ianaIds: readonly string[],
  browserTimezone: string,
  browserRowKey: string
): TimezoneSelectOption[] {
  const seenUsGroups = new Set<UsTimezoneGroup>();

  return ianaIds
    .map((tz) => {
      const group = US_TIMEZONE_GROUP_BY_IANA[tz];
      if (group) {
        if (seenUsGroups.has(group)) {
          return undefined;
        }
        seenUsGroups.add(group);
        const value = CANONICAL_US_TIMEZONE_BY_GROUP[group];
        const baseLabel = US_TIMEZONE_LABEL_BY_GROUP[group];
        return {
          value,
          label: value === browserRowKey ? `${baseLabel} - Your Time zone` : baseLabel,
        };
      }
      return {
        value: tz,
        label: tz === browserTimezone ? `${tz} - Your Time zone` : tz,
      };
    })
    .filter((option): option is TimezoneSelectOption => !!option);
}

/**
 * All supported IANA timezones for select inputs.
 * US zones: one option per group (no duplicate friendly labels); value is the canonical IANA for that group.
 * Other zones: one option each; value and label are the IANA id.
 */
export function generateTimezoneOptions(): TimezoneSelectOption[] {
  const browserTimezone = getBrowserTimezone();
  const browserRowKey = normalizeUsTimezoneToCanonical(browserTimezone);
  const options = groupUsTimezonesToSelectOptions(Intl.supportedValuesOf('timeZone'), browserTimezone, browserRowKey);

  return options.sort((a, b) => {
    if (a.value === browserRowKey) return -1;
    if (b.value === browserRowKey) return 1;

    const aRank = usRank.get(normalizeUsTimezoneToCanonical(a.value));
    const bRank = usRank.get(normalizeUsTimezoneToCanonical(b.value));

    if (aRank !== undefined && bRank !== undefined && aRank !== bRank) return aRank - bRank;
    if (aRank !== undefined && bRank === undefined) return -1;
    if (aRank === undefined && bRank !== undefined) return 1;

    return a.value.localeCompare(b.value);
  });
}

/**
 * Calculates the date range based on the calendar view and current date.
 */
export function getDateRange(date: Date, view: View, timezone?: string): { startDate: string; endDate: string } {
  const current = timezone
    ? dayjs.tz(
        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T12:00:00`,
        timezone
      )
    : dayjs(date);

  switch (view) {
    case 'day':
      return {
        startDate: current.startOf('day').toISOString(),
        endDate: current.endOf('day').toISOString(),
      };
    case 'week':
      return {
        startDate: current.startOf('week').toISOString(),
        endDate: current.endOf('week').toISOString(),
      };
    case 'work_week':
      // Work week: Monday to Friday
      return {
        startDate: current.startOf('week').add(1, 'day').toISOString(),
        endDate: current.startOf('week').add(5, 'day').endOf('day').toISOString(),
      };
    case 'month':
      // For month view, include some buffer days for calendar display
      return {
        startDate: current.startOf('month').startOf('week').toISOString(),
        endDate: current.endOf('month').endOf('week').toISOString(),
      };
    case 'agenda':
      // Agenda view typically shows 30 days
      return {
        startDate: current.startOf('day').toISOString(),
        endDate: current.add(30, 'day').endOf('day').toISOString(),
      };
    default:
      return {
        startDate: current.startOf('week').toISOString(),
        endDate: current.endOf('week').toISOString(),
      };
  }
}

/**
 * Maps practitioner resource IDs to their corresponding `PractitionerRole` references.
 */
export function mapPractitionerIdsToProviderRefs(
  ids: string[],
  practitioners: Practitioner[],
  practitionerRoles: PractitionerRole[]
): string[] {
  return ids
    .map((id) => {
      const practitioner = practitioners.find((p) => p.id === id);
      if (!practitioner) return null;

      const role = practitionerRoles.find((r) => r.practitioner?.reference === getReferenceString(practitioner));
      return role ? getReferenceString(role)! : null;
    })
    .filter((ref): ref is string => !!ref);
}
